import { Alert, AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { maxLength } from 'utils/validation-utils'
import { validationContext } from 'validx'
import { Id } from 'types/definitions'
import { FindMembers } from 'iam/MemberStore'
import ClientInputEditorViewModel from './TransferToClient/ClientInputEditorViewModel'
import ProInputEditorViewModel from './TransferToPro/ProInputEditorViewModel'
import ClientTeamInputEditorViewModel from './TransferToClientTeam/ClientTeamInputEditorViewModel'
import { FindUserTeamMembersByUserPublicId } from 'iam/TeamMemberStore'
import ProviderStore from 'jobs/ProviderStore'
import RatingsViewModel from '../../../../ratings/RatingsViewModel'
import BulkActionJobErrorViewModel from '../../../BulkActionJobErrorDialog/BulkActionJobErrorViewModel'

export const enum TransferType {
  Pool = 'POOL',
  Pro = 'PRO',
  Client = 'CLIENT',
  Team = 'TEAM',
  None = 'NONE',
}

/**
 * Represents the shape of transfer job options.
 */
export interface TransferJobOption {
  displayName: string
  transferType: TransferType
}

/**
 * View Model for the Transfer Job dialog.
 * @extends ModalViewModel
 */
export default class TransferJobViewModel extends ModalViewModel {
  @observable
  jobs: Job[] = []

  /**
   * Holds the maximum length of each field.
   */
  private readonly fieldLengths = {
    reason: { max: 100 },
  }

  /**
   * Validation context for the view model.
   */
  validation = validationContext(this, {
    reason: [maxLength(this.fieldLengths.reason.max)],
  })

  /**
   * Helper text for reason field by Transfer Type.
   */
  @computed
  get showReasonTextBox() {
    switch (this.transferType) {
      case TransferType.Client:
      case TransferType.Pro:
      case TransferType.Team:
        return `Why are you transferring?
        This text is public and can be seen by the Client and Pros.`
      default:
        return `Why are you transferring to the pool? This text is public
      and can be seen by the Client and Pros.`
    }
  }

  /**
   * Default option selection.
   */
  defaultOption: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
  ]

  /**
   * Consultation option selection.
   */
  consultationOptions: TransferJobOption[] = [
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
    {
      displayName: 'Pro',
      transferType: TransferType.Pro,
    },
  ]

  /**
   * Client and pool options selection.
   */
  poolAndClientOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
  ]

  /**
   * Client and team option selection.
   */
  unclaimedOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Team',
      transferType: TransferType.Team,
    },
  ]

  /**
   * Client and team option selection.
   */
  noProOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
    {
      displayName: 'Team',
      transferType: TransferType.Team,
    },
  ]

  /**
   * Client and team option selection.
   */
  noTeamOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
    {
      displayName: 'Pro',
      transferType: TransferType.Pro,
    },
  ]

  /**
   * Options for Transfer selection.
   */
  allOptions: TransferJobOption[] = [
    {
      displayName: 'Client',
      transferType: TransferType.Client,
    },
    {
      displayName: 'Pool',
      transferType: TransferType.Pool,
    },
    {
      displayName: 'Pro',
      transferType: TransferType.Pro,
    },
    {
      displayName: 'Team',
      transferType: TransferType.Team,
    },
  ]

  /**
   * When the selected jobs have no transfer option in common
   * */
  noneOption: TransferJobOption[] = [
    {
      displayName: '',
      transferType: TransferType.None,
    },
  ]

  /**
   * The transfer type.
   */
  @observable
  transferType: TransferType = TransferType.None

  /**
   * Registry of detail view models for each possible type of transfer.
   */
  readonly transferViewModels = {
    [TransferType.Client]: new ClientInputEditorViewModel(this.findMembers),
    [TransferType.Pro]: new ProInputEditorViewModel(
      this.findMembers,
      this.providerStore,
    ),
    [TransferType.Team]: new ClientTeamInputEditorViewModel(
      this.findTeamMembersByUserPublicId,
    ),
    [TransferType.Pool]: null,
  } as const

  /**
   * The reason of the transfer.
   */
  @observable
  reason = ''

  /**
   * Disables transfer button.
   */
  @observable
  disabled = true

  constructor(
    private findMembers: FindMembers,
    private findTeamMembersByUserPublicId: FindUserTeamMembersByUserPublicId,
    private jobStore: JobStore,
    private providerStore: ProviderStore,
    private newAlert: AlertFactory,
    private ratingsVM?: RatingsViewModel,
    private bulkActionErrorVM?: BulkActionJobErrorViewModel,
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)
    this.show$.subscribe(this.shown)
  }

  /**
   * Transfer options depending on permission.
   */
  @computed
  get filteredTransferOptions() {
    return this.jobs.length === 1 ? this.forSingleJob : this.forMultiJobs
  }

  @computed
  get forSingleJob() {
    const job = this.jobs[0]

    if (!job.canTransferConsultationJobAsAdmin) {
      return this.noneOption
    }

    const poolTransferOption = job.canTransferToPoolAsAdmin
    const clientTransferOption = job.canTransferToClientAsAdmin
    const proTransferOption = job.canTransferToProAsAdmin
    const clientTeamTransferOption = job.canTransferToClientTeamAsAdmin
    const canSeeAllOptions =
      clientTransferOption &&
      proTransferOption &&
      clientTeamTransferOption &&
      poolTransferOption
    const canSeeAllExceptProOptions =
      !proTransferOption &&
      clientTransferOption &&
      clientTeamTransferOption &&
      poolTransferOption
    const canSeeAllExceptTeamOptions =
      proTransferOption &&
      clientTransferOption &&
      !clientTeamTransferOption &&
      poolTransferOption

    // If we have a consultation only allow transfer to the pool and provider.
    if (job.hasActiveConsultation) {
      return this.consultationOptions
    }

    // We check these just in case.
    if (job.isClaimedJob && canSeeAllExceptProOptions) {
      return this.noProOptions
    }

    if (job.isClaimedJob && canSeeAllExceptTeamOptions) {
      return this.noTeamOptions
    }

    // Display all transfer options.
    if (job.isClaimedJob && canSeeAllOptions) {
      return this.allOptions
    }

    // Job on hold with champion provider still shows all transfer options.
    if (job.status === 'ON_HOLD' && job.championProvider && canSeeAllOptions) {
      return this.allOptions
    }

    // In this case, we only show client/team transfer options.
    if (!job.isClaimedJob && clientTeamTransferOption && clientTransferOption) {
      return this.unclaimedOptions
    }

    if (job.isClaimedJob && poolTransferOption && clientTransferOption) {
      return this.poolAndClientOptions
    }

    return this.defaultOption
  }

  @computed
  get forMultiJobs() {
    if (this.jobs.some((j) => !j.canTransferConsultationJobAsAdmin)) {
      return this.noneOption
    }

    const clientTransferOption = this.jobs.every(
      (j) => j.canTransferToClientAsAdmin,
    )

    const clientTeamTransferOption = this.jobs.every(
      (p) =>
        p.canTransferToClientTeamAsAdmin && p.jobType?.type !== 'CONSULTATION',
    )

    const poolTransferOption = this.jobs.every(
      (job) =>
        job.canTransferToPoolAsAdmin &&
        job.proChampion &&
        !job.completed &&
        !job.isDraft,
    )

    const proTransferOption = this.jobs.every(
      (job) =>
        job.canTransferToProAsAdmin &&
        job.proChampion &&
        !job.completed &&
        !job.isDraft,
    )

    const conditions = [
      {
        displayName: 'Pool',
        transferType: TransferType.Pool,
        enabled: poolTransferOption,
      },
      {
        displayName: 'Team',
        transferType: TransferType.Team,
        enabled: clientTeamTransferOption,
      },
      {
        displayName: 'Client',
        transferType: TransferType.Client,
        enabled: clientTransferOption,
      },
      {
        displayName: 'Pro',
        transferType: TransferType.Pro,
        enabled: proTransferOption,
      },
    ]

    const results = conditions.filter((c) => c.enabled)

    return results.length > 0 ? results : this.noneOption
  }

  /**
   * The currently selected transfer type.
   */
  @computed
  get option() {
    return this.allOptions.find((o) => o.transferType === this.transferType)
  }

  @computed
  get showRateProButton() {
    return this.ratingsVM?.canRatePro
  }

  showProRatingDialog = () => {
    this.ratingsVM?.showRateProDialog()
  }

  /**
   * Shows Transfer Job Dialog.
   */
  @action
  open(jobs: Job[]) {
    if (this.showing) {
      return
    }

    this.jobs = jobs

    super.show()
  }

  /**
   * Transfers job by Transfer Type. It could be to the Pool, Pro, Client or Team.
   */
  transfer = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    switch (this.transferType) {
      case TransferType.Pool:
        await this.transferJobToPool()
        break
      case TransferType.Client:
        await this.transferJobToClient()
        break
      case TransferType.Pro:
        await this.transferJobToPro()
        break
      case TransferType.Team:
        await this.transferJobToClientTeam()
        break
      default:
        return Promise.reject(new Error('Transfer type not supported.'))
    }

    this.close()
  })

  handleBulkResponse(
    alert: Alert,
    response: { jobId: string; error: string }[],
    target?: string,
  ) {
    const errorList = response.map((error) => {
      const jobInfo = this.jobs.find((j) => j.id === error.jobId)
      return {
        error: error.error,
        jobId: jobInfo?.shortId || '',
        jobTitle: jobInfo?.displayName || '',
      }
    })

    if (errorList.length > 0) {
      this.bulkActionErrorVM?.showErrors(errorList)
    }

    // all selected jobs failed the transfer so don't show the success alert
    if (this.jobs.length === errorList.length) {
      alert.dismiss()
      return
    }

    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${this.jobs.length - errorList.length} jobs were transferred to ${target}`,
      )

    this.close()
  }

  /**
   * Transfers job to the pool.
   */
  async transferJobToPool() {
    const jobsCount = this.jobs.length

    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Transferring to pool',
        `Transferring ${jobsCount === 1 ? this.jobs[0].name : `${jobsCount} jobs`} to Pool...`,
      )
      .autoDismiss()
      .show()

    if (this.jobs.length === 1) {
      await this.jobStore.transferJobToPoolAsAdmin(
        Id.assert(this.jobs[0].id),
        this.reason,
      )

      alert
        .withState(AlertState.Success)
        .saying('Success!', `${this.jobs[0].name} was transferred to Pool...`)

      return
    }

    const response = await this.jobStore.bulkTransferJobsToPool(
      this.jobs.map((j) => Id.assert(j.id)),
      this.reason,
    )

    this.handleBulkResponse(alert, response, 'Pool')
  }

  /**
   * Transfer to client.
   */
  transferJobToClient = task.resolved(async () => {
    const jobsCount = this.jobs.length

    const client = this.transferViewModels[TransferType.Client].client

    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Transferring to new client',
        `Transferring ${
          client?.displayName
        } to ${jobsCount === 1 ? this.jobs[0].name : `${jobsCount} jobs`}...`,
      )
      .autoDismiss()
      .show()

    if (this.jobs.length === 1) {
      await this.jobStore.transferJobToClientAsAdmin(
        Id.assert(this.jobs[0].id),
        Id.assert(client?.user.publicId),
        this.reason,
      )

      alert
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${client?.displayName} was transferred to ${this.jobs[0].name}.`,
        )
        .autoDismiss()

      return
    }

    const response = await this.jobStore.bulkTransferJobsToClientAsAdmin(
      this.jobs.map((j) => Id.assert(j.id)),
      Id.assert(
        this.transferViewModels[TransferType.Client].client?.user.publicId,
      ),
      this.reason,
    )

    this.handleBulkResponse(alert, response, client?.displayName)
  })

  /**
   * Transfer to pro.
   */
  transferJobToPro = task.resolved(async () => {
    const proVM = this.transferViewModels[TransferType.Pro]

    const jobsCount = this.jobs.length

    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Transferring to new pro',
        `Transferring ${proVM.pro?.displayName} to ${this.jobs[0].name}...`,
      )
      .autoDismiss()
      .show()

    if (jobsCount === 1) {
      await this.jobStore.transferJobToProAsAdmin(
        Id.assert(this.jobs[0].id),
        Id.assert(proVM.pro?.user.publicId),
        this.reason,
      )
      alert
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${proVM.pro?.displayName} was transferred to ${this.jobs[0].name}.`,
        )
        .autoDismiss()

      return
    }

    const response = await this.jobStore.bulkTransferJobsToProviderAsAdmin(
      this.jobs.map((job) => Id.assert(job.id)),
      Id.assert(proVM.pro?.user.publicId),
      this.reason,
    )

    this.handleBulkResponse(alert, response, proVM.pro?.displayName)
  })

  /**
   * Transfer to client team.
   */
  transferJobToClientTeam = task.resolved(async () => {
    const jobsCount = this.jobs.length

    const clientTeamName =
      this.transferViewModels[TransferType.Team].clientTeamMember?.team.name

    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Transferring to new client team',
        `Transferring ${jobsCount === 1 ? this.jobs[0].name : `${jobsCount} jobs`} to ${
          clientTeamName
        }...`,
      )
      .autoDismiss()
      .show()

    if (this.jobs.length === 1) {
      await this.jobStore.transferJobToClientTeamAsAdmin(
        Id.assert(this.jobs[0].id),
        Id.assert(
          this.transferViewModels[TransferType.Team].clientTeamMember?.team.id,
        ),
        this.reason,
      )

      alert
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${this.jobs[0].name} was transferred to ${clientTeamName}....`,
        )

      return
    }

    const response = await this.jobStore.bulkTransferJobToClientTeamAsAdmin(
      this.jobs.map((job) => Id.assert(job.id)),
      Id.assert(
        this.transferViewModels[TransferType.Team].clientTeamMember?.team.id,
      ),
      this.reason,
    )

    this.handleBulkResponse(alert, response, clientTeamName)
  })

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    const isThisViewModelValid = this.validation.reset().validate().isValid
    const isClientViewModelValid =
      this.transferViewModels[TransferType.Client].validateClient()
    const isProViewModelValid =
      this.transferViewModels[TransferType.Pro].validate()
    const isClientTeamViewModelValid =
      this.transferViewModels[TransferType.Team].validateClientTeamMember()

    switch (this.transferType) {
      case TransferType.Client:
        return isThisViewModelValid && isClientViewModelValid
      case TransferType.Pro:
        return isThisViewModelValid && isProViewModelValid
      case TransferType.Team:
        return isThisViewModelValid && isClientTeamViewModelValid
      default:
        return isThisViewModelValid
    }
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.jobs = []
    this.reason = ''
    this.disabled = true
    this.transferType = TransferType.None
    this.transfer.reset()

    this.validation.reset()
    Object.values(this.transferViewModels).forEach((vm) => vm?.reset())
    Object.values(this.transferViewModels).forEach((vm) => vm?.deactivate())
  }

  /**
   * Activates the member search field with a debounce time.
   */
  @action.bound
  shown() {
    Object.values(this.transferViewModels).forEach((vm) =>
      vm?.activate(this.jobs),
    )
  }

  /**
   * Sets the transfer type.
   */
  @action.bound
  async setTransferOption(transferOption?: TransferJobOption) {
    if (transferOption) {
      if (transferOption.transferType === TransferType.Pro) {
        this.ratingsVM?.fetchJobRatingStatus().then()
      }

      this.transferType = transferOption.transferType
    }
  }
}
